import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Database, RefreshCw, ChevronDown, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Badge } from '../ui/badge';
import { crmAPI } from '../../lib/api';

const STATUS_CONFIG = {
  healthy: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle2, label: 'Healthy', dot: 'bg-emerald-500' },
  warning: { color: 'text-yellow-600', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: AlertTriangle, label: 'Warning', dot: 'bg-yellow-500' },
  critical: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, label: 'Critical', dot: 'bg-red-500' },
};

function formatAge(hours) {
  if (hours < 1) return `${Math.round(hours * 60)}m ago`;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function TrustPill({ label, status, detail }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.warning;
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md ${cfg.bg} ${cfg.border} border`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      <span className="text-[11px] font-medium text-gray-700">{label}</span>
      {detail && <span className={`text-[10px] ${cfg.color}`}>{detail}</span>}
    </div>
  );
}

function SectionCard({ title, icon: Icon, status, children }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.warning;
  return (
    <div className={`rounded-lg border ${cfg.border} ${cfg.bg} p-3`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
          <span className="text-xs font-semibold text-gray-700">{title}</span>
        </div>
        <Badge variant="outline" className={`text-[9px] ${cfg.border} ${cfg.color} ${cfg.bg}`}>{cfg.label}</Badge>
      </div>
      {children}
    </div>
  );
}

export default function TrustControl() {
  const [trust, setTrust] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await crmAPI.getTrustScore();
      setTrust(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, [load]);

  if (!trust) return null;

  const cfg = STATUS_CONFIG[trust.overall_status] || STATUS_CONFIG.warning;
  const StatusIcon = cfg.icon;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all hover:shadow-sm ${cfg.border} ${cfg.bg}`}
          data-testid="trust-control-trigger"
        >
          <StatusIcon className={`h-3.5 w-3.5 ${cfg.color}`} />
          <span className={`text-xs font-semibold ${cfg.color}`}>{trust.overall_score}%</span>
          <ChevronDown className="h-3 w-3 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="end" data-testid="trust-control-panel">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-lg ${cfg.bg} ${cfg.border} border flex items-center justify-center`}>
              <Shield className={`h-4 w-4 ${cfg.color}`} />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">Data Trust</p>
              <p className="text-[10px] text-gray-400">System health overview</p>
            </div>
          </div>
          <div className={`text-2xl font-bold ${cfg.color}`}>{trust.overall_score}%</div>
        </div>

        {/* Quick Pills */}
        <div className="flex gap-1.5 mb-3 flex-wrap">
          <TrustPill label="Rules" status={trust.rules.status} detail={`${trust.rules.rbac_users} users`} />
          <TrustPill label="Freshness" status={trust.freshness.status} detail={formatAge(trust.freshness.freshest_hours)} />
          <TrustPill label="Sync" status={trust.sync.status} detail={`${trust.sync.record_counts.opportunities} opps`} />
        </div>

        {/* Sections */}
        <div className="space-y-2">
          <SectionCard title="Access Rules" icon={Shield} status={trust.rules.status}>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="text-sm font-bold text-gray-800">{trust.rules.rbac_users}</p><p className="text-[9px] text-gray-500">RBAC Users</p></div>
              <div><p className="text-sm font-bold text-gray-800">{trust.rules.app_users}</p><p className="text-[9px] text-gray-500">App Users</p></div>
              <div><p className="text-sm font-bold text-gray-800">{trust.rules.roles_defined}</p><p className="text-[9px] text-gray-500">Roles</p></div>
            </div>
          </SectionCard>

          <SectionCard title="Data Freshness" icon={Database} status={trust.freshness.status}>
            <div className="space-y-1">
              {(trust.freshness.entities || []).filter(e => e.is_critical).map(e => (
                <div key={e.entity} className="flex items-center justify-between text-[11px]">
                  <span className="text-gray-600 capitalize">{e.entity}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-500">{e.records_synced} rec</span>
                    <span className={e.is_stale ? 'text-red-500 font-medium' : 'text-emerald-600'}>
                      {formatAge(e.age_hours)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard title="Sync Status" icon={RefreshCw} status={trust.sync.status}>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="text-sm font-bold text-gray-800">{trust.sync.record_counts.opportunities}</p><p className="text-[9px] text-gray-500">Opportunities</p></div>
              <div><p className="text-sm font-bold text-gray-800">{trust.sync.record_counts.sales_orders}</p><p className="text-[9px] text-gray-500">Sales Orders</p></div>
              <div><p className="text-sm font-bold text-gray-800">{trust.sync.record_counts.invoices}</p><p className="text-[9px] text-gray-500">Invoices</p></div>
            </div>
            {trust.sync.sync_needed && (
              <p className="text-[10px] text-yellow-600 mt-1.5 font-medium">Cache stale — new data available</p>
            )}
          </SectionCard>
        </div>

        {/* Issues */}
        {trust.issues?.length > 0 && (
          <div className="mt-2 space-y-1">
            {trust.issues.map((issue, idx) => (
              <div key={idx} className="flex items-center gap-1.5 text-[10px]">
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[issue.severity === 'critical' ? 'critical' : 'warning'].dot}`} />
                <span className="text-gray-600">{issue.message}</span>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
